<template>
  <fullscreen-dialog
    v-model="model"
    :title="dialogTitle"
    icon="mdi-cog"
    color="deep-orange"
    @close="handleClose"
  >
    <v-row>
      <!-- Left Column -->
      <v-col cols="12" md="6">
        <delivery-switches
          v-if="model"
          ref="deliverySwitches"
          :restaurant="selectedRestaurant"
          :company="selectedCompany"
        />

        <!-- Time Settings -->
        <time-settings-card
          v-model="localDeliveryTime"
          :restaurant="selectedRestaurant"
          :company="selectedCompany"
        />

        <!-- Limits -->
        <!-- <limits-card
          v-model="localLimits"
          :restaurant="selectedRestaurant"
          :company="selectedCompany"
          :price="localPrice"
          :can-order-extra="localCanOrderExtra"
          @save="save"
        /> -->
      </v-col>

      <!-- Right Column -->
      <v-col cols="12" md="6">
        <!-- Menu Type -->
        <menu-type-card
          :restaurant="selectedRestaurant"
          :company="selectedCompany"
          :picked-id="pickedMenuTypeId"
          @pick="pickedMenuTypeId = $event"
        />

        <!-- Delivery Locations -->
        <delivery-locations-card
          :restaurant="selectedRestaurant"
          :company="selectedCompany"
          :delivery-time="localDeliveryTime"
        />
        <!-- Week Days Schedule -->
        <week-schedule-card :restaurant="selectedRestaurant" />

        <slot></slot>
      </v-col>
    </v-row>

    <template #footer-actions>
      <primary-button color="deep-orange" :loading="saving" @click="save">
        <v-icon left>mdi-content-save</v-icon>
        {{ $t("save") }}
      </primary-button>
    </template>
  </fullscreen-dialog>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import hasModel from "@/mixins/hasModel";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import companyService from "@/services/company";
import {
  DeliveryLocationsCard,
  TimeSettingsCard,
  LimitsCard,
  MenuTypeCard,
  WeekScheduleCard,
  DeliverySwitches,
} from "./delivery-settings";

/**
 * Stanje podešavanja veze onako kako je stiglo sa servera, po objektu restorana. Karticama (smjene, rokovi...) se
 * struktura dopunjuje sama, pa se poredi samo suština vrijednosti, bez praznih podrazumijevanih djelova.
 */
const loadedSettings = new WeakMap();

function essence(value) {
  if (Array.isArray(value)) {
    const items = value.map(essence);
    return items.every((item) => item === undefined) ? undefined : items;
  }
  if (value && typeof value === "object") {
    const result = {};
    Object.keys(value)
      .sort()
      .forEach((key) => {
        const inner = essence(value[key]);
        if (inner !== undefined) {
          result[key] = inner;
        }
      });
    return Object.keys(result).length ? result : undefined;
  }
  return value === null || value === "" || value === false ? undefined : value;
}

const sameValue = (a, b) => JSON.stringify(essence(a)) === JSON.stringify(essence(b));

export default {
  name: "CompanyDeliverySettingsDialog",
  mixins: [hasModel, hasSnackbar],
  components: {
    FullscreenDialog,
    DeliveryLocationsCard,
    TimeSettingsCard,
    LimitsCard,
    MenuTypeCard,
    WeekScheduleCard,
    DeliverySwitches,
  },
  props: {
    selectedRestaurant: {
      type: Object,
      default: null,
    },
    selectedCompany: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      pickedMenuTypeId: null,
      localDeliveryTime: "no_delivery_time",
      localLimits: null,
      localPrice: 0,
      localCanOrderExtra: false,
      saving: false,
    };
  },
  computed: {
    dialogTitle() {
      const restaurant = this.toLatin(this.selectedRestaurant?.name || "");
      const company = this.toLatin(this.selectedCompany?.name || "");
      return `${restaurant} / ${company}`;
    },
  },
  watch: {
    selectedRestaurant: {
      immediate: true,
      handler(val) {
        if (val) {
          this.initializeFromRestaurant();
        }
      },
    },
    localDeliveryTime(val) {},
  },
  methods: {
    /** Samo podešavanja koja je korisnik promijenio; tip jelovnika se snima posebno, po ID-u. */
    changedSettings() {
      const current = this.selectedRestaurant?.company_restaurant_settings || {};
      const original = loadedSettings.get(this.selectedRestaurant) || {};
      const changed = {};
      Object.keys(current).forEach((key) => {
        if (key !== "menu_types_id" && !sameValue(current[key], original[key])) {
          changed[key] = current[key];
        }
      });
      return changed;
    },
    async save() {
      const settings = this.changedSettings();
      const hasSettings = Object.keys(settings).length > 0;
      const menuTypeId = this.pickedMenuTypeId;
      if (!hasSettings && menuTypeId == null) {
        this.showInfoSnackbar(this.$t("nothing_to_save"));
        return;
      }

      const companyId = this.selectedCompany.id;
      const restaurantId = this.selectedRestaurant.id;
      let saved = false;
      this.saving = true;
      try {
        if (hasSettings) {
          await companyService.setResturantOptions(companyId, restaurantId, settings);
          saved = true;
        }
        if (menuTypeId != null) {
          await companyService.setMenuType(companyId, restaurantId, {
            menu_types_id: menuTypeId,
          });
          saved = true;
        }
        this.showSuccessSnackbar(this.$t("settings_saved"));
      } catch (error) {
        this.showErrorSnackbar(
          error?.response?.data?.message || this.$t("error_saving_settings")
        );
      } finally {
        this.saving = false;
        if (saved) {
          // Ekran prikazuje stanje sa servera, ne ono što smo mi mislili da smo snimili
          this.$emit("saved");
        }
      }
    },
    initializeFromRestaurant() {
      if (!this.selectedRestaurant) return;

      this.pickedMenuTypeId = null;

      // Normalize type_of_delivery — API returns [] when empty, needs to be an object
      if (
        !this.selectedRestaurant.company_restaurant_settings.type_of_delivery ||
        Array.isArray(
          this.selectedRestaurant.company_restaurant_settings.type_of_delivery
        )
      ) {
        this.$set(
          this.selectedRestaurant.company_restaurant_settings,
          "type_of_delivery",
          {
            take_by_user: false,
            restaurant_eat: false,
            delivery_to_firm: false,
            delivery_to_user_address: false,
          }
        );
      }

      if (!loadedSettings.has(this.selectedRestaurant)) {
        loadedSettings.set(
          this.selectedRestaurant,
          JSON.parse(JSON.stringify(this.selectedRestaurant.company_restaurant_settings))
        );
      }

      // Initialize limits from voucher
      if (this.selectedRestaurant.voucher) {
        const limitType = this.selectedRestaurant.voucher.params?.limit?.type;
        if (limitType === "meals_per_day") {
          this.localLimits = "obrok";
        } else if (limitType === "meals_per_shift") {
          this.localLimits = "smena";
        } else if (limitType === "amount") {
          this.localLimits = "firma";
        } else if (limitType === "bon") {
          this.localLimits = "bon";
        } else if (limitType === "student") {
          this.localLimits = "student";
        }
        this.localCanOrderExtra =
          this.selectedRestaurant.voucher.params?.limit?.can_order_extra || false;
      }

      // Initialize delivery settings
      if (this.selectedRestaurant.delivery_setting) {
        this.localDeliveryTime =
          this.selectedRestaurant.delivery_setting.delivery || "no_delivery_time";
      } else {
        this.localDeliveryTime = "no_delivery_time";
      }
    },
    async handleClose() {
      this.model = false;
      this.$emit("close");
    },
  },
};
</script>

<style>
/* Radio button alignment - unscoped for Vuetify components */
.fullscreen-dialog .v-radio {
  align-items: center !important;
}

.fullscreen-dialog .v-radio .v-input--selection-controls__input {
  align-self: center !important;
  margin-top: 0 !important;
}

.fullscreen-dialog .v-radio .v-label {
  display: flex;
  align-items: center !important;
}

.fullscreen-dialog .v-input--selection-controls__ripple {
  top: calc(50% - 24px) !important;
}
</style>
